import { useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { DialogTitle } from '@savvy-kit/components/dialog';
import { Label } from '@savvy-kit/components/label';
import { Textarea } from '@savvy-kit/components/textarea';
import { Text } from '@savvy-kit/foundations/text';
import { AttachFileButton, FilePreviewList, WithFileUpload } from '@savvy-kit/patterns/file-attachment';
import { toast } from 'sonner';
import { uploadToCloudinary } from 'src/components/advisor-dashboard/report-issue/upload-to-cloudinary';
import TrackedDialog from 'src/components/tracked/tracked-dialog';
import { apiFetch } from 'src/utils/api-fetch';
import { buildAuthHeaders } from 'src/utils/auth-headers';
import { handleMutationError } from 'src/utils/handle-mutation-error';
import { DatadogRumAction } from 'src/utils/logging';
import { z } from 'zod';

import { AddAccountFormShell } from '../accounts/add-account/add-account-form-shell';
import { CLIENT_PORTAL_DIALOG_PANEL_CLASS } from '../common/constants';
import {
  REPORT_ISSUE_ALLOWED_IMAGE_EXTENSIONS,
  REPORT_ISSUE_ATTACHMENT_LABEL,
  REPORT_ISSUE_DESCRIPTION_HINT,
  REPORT_ISSUE_DESCRIPTION_LABEL,
  REPORT_ISSUE_DESCRIPTION_PLACEHOLDER,
  REPORT_ISSUE_DONE_LABEL,
  REPORT_ISSUE_ENDPOINT,
  REPORT_ISSUE_MAX_DESCRIPTION_LENGTH,
  REPORT_ISSUE_MAX_IMAGE_BYTES,
  REPORT_ISSUE_SUBMIT_ERROR,
  REPORT_ISSUE_SUBMIT_LABEL,
  REPORT_ISSUE_TIMEOUT_MS,
  REPORT_ISSUE_TITLE,
  reportIssueConfirmation,
} from './constants';

type ClientPortalReportIssueDialogProps = {
  readonly onClose: () => void;
};

const submitResultSchema = z.object({ error: z.string().optional(), issueIdentifier: z.string().optional() });

async function submitReport(description: string, file: File | null, signal: AbortSignal): Promise<string | null> {
  const imageUrl = file === null ? undefined : await uploadToCloudinary(file);
  signal.throwIfAborted();
  const response = await apiFetch(REPORT_ISSUE_ENDPOINT, {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: JSON.stringify({ description, imageUrl }),
    signal,
  });
  const json: unknown = await response.json().catch(() => ({}));
  const result = submitResultSchema.catch({}).parse(json);
  if (!response.ok) {
    throw new Error(result.error ?? REPORT_ISSUE_SUBMIT_ERROR);
  }
  return result.issueIdentifier ?? null;
}

function withTimeout<T>(start: (signal: AbortSignal) => Promise<T>, timeoutMs: number): Promise<T> {
  const controller = new AbortController();
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      controller.abort();
      reject(new Error(REPORT_ISSUE_SUBMIT_ERROR));
    }, timeoutMs);
    start(controller.signal)
      .then(resolve, reject)
      .finally(() => {
        window.clearTimeout(timer);
      });
  });
}

export function ClientPortalReportIssueDialog({ onClose }: ClientPortalReportIssueDialogProps) {
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reference, setReference] = useState<string | null>(null);

  const trimmedDescription = description.trim();

  async function submit() {
    if (trimmedDescription === '' || submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const identifier = await withTimeout(
        (signal) => submitReport(trimmedDescription, files[0] ?? null, signal),
        REPORT_ISSUE_TIMEOUT_MS,
      );
      datadogRum.addAction(DatadogRumAction.ReportIssueSubmitted, { source: 'client_portal' });
      setReference(identifier);
      setSubmitted(true);
    } catch (error) {
      handleMutationError(error, REPORT_ISSUE_SUBMIT_ERROR);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <TrackedDialog
      elementName="client-portal-report-issue-dialog"
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !submitting) {
          onClose();
        }
      }}
      className={CLIENT_PORTAL_DIALOG_PANEL_CLASS}
    >
      <DialogTitle className="sr-only">{REPORT_ISSUE_TITLE}</DialogTitle>
      {submitted ? (
        <AddAccountFormShell title={REPORT_ISSUE_TITLE} onCancel={onClose} cancelLabel={REPORT_ISSUE_DONE_LABEL}>
          <Text variant="body-sm" role="status">
            {reportIssueConfirmation(reference)}
          </Text>
        </AddAccountFormShell>
      ) : (
        <AddAccountFormShell
          title={REPORT_ISSUE_TITLE}
          onCancel={onClose}
          onSubmit={() => {
            void submit();
          }}
          submitLabel={REPORT_ISSUE_SUBMIT_LABEL}
          submitting={submitting}
          submitDisabled={trimmedDescription === ''}
          cancelDisabled={submitting}
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="client-portal-report-issue-description">{REPORT_ISSUE_DESCRIPTION_LABEL}</Label>
            <Textarea
              id="client-portal-report-issue-description"
              autoFocus
              rows={6}
              value={description}
              maxLength={REPORT_ISSUE_MAX_DESCRIPTION_LENGTH}
              placeholder={REPORT_ISSUE_DESCRIPTION_PLACEHOLDER}
              aria-describedby="client-portal-report-issue-description-hint"
              onChange={(event) => {
                setDescription(event.target.value);
              }}
            />
            <Text id="client-portal-report-issue-description-hint" variant="caption">
              {REPORT_ISSUE_DESCRIPTION_HINT}
            </Text>
          </div>
          <WithFileUpload
            files={files}
            onRemove={() => {
              setFiles([]);
            }}
            allowedExtensions={REPORT_ISSUE_ALLOWED_IMAGE_EXTENSIONS}
            maxSizeBytes={REPORT_ISSUE_MAX_IMAGE_BYTES}
            onFilesChange={(next) => {
              setFiles(next.slice(-1));
            }}
            onFileError={(message) => toast.error(message)}
            disabled={submitting}
          >
            <div className="flex flex-col gap-2">
              <Label>{REPORT_ISSUE_ATTACHMENT_LABEL}</Label>
              <AttachFileButton />
              <FilePreviewList />
            </div>
          </WithFileUpload>
        </AddAccountFormShell>
      )}
    </TrackedDialog>
  );
}
